import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PUSH_NOTIFICATION_MESSAGES } from "~/constants/messages/pushNotificationMessages";
import { TOAST_MESSAGES } from "~/constants/messages/toastMessages";

// 通知許可の状態を表す型
type PermissionState = "default" | "granted" | "denied" | "unsupported";

// 通知データの型定義
export interface NotificationData {
  title?: string | undefined;
  message: string;
  icon?: string | undefined;
  url?: string | undefined;
  organization_id?: string | undefined;
  project_id?: string | undefined;
  document_type?: string | undefined;
  document_id?: string | undefined;
}

export const useNativePushNotification = () => {
  const [permission, setPermission] = useState<PermissionState>("default");

  const postMessageToServiceWorker = useCallback(async (payload: Record<string, unknown>) => {
    if (!("serviceWorker" in navigator)) return;

    const controller = navigator.serviceWorker.controller;
    if (controller) {
      controller.postMessage(payload);
      return;
    }

    try {
      let registration = await navigator.serviceWorker.getRegistration?.();
      if (!registration) {
        try {
          registration = await navigator.serviceWorker.ready;
        } catch {
          // ignore
        }
      }
      const sw = registration?.active || registration?.waiting || registration?.installing;
      sw?.postMessage(payload);
    } catch {
      // ignore
    }
  }, []);

  // Service Worker の登録と通知許可状態の取得
  useEffect(() => {
    // Service Worker または Notification API がサポートされているか確認
    if (!("serviceWorker" in navigator) || !("Notification" in window)) {
      setPermission("unsupported");
      return;
    }
    const registerServiceWorker = async () => {
      try {
        await navigator.serviceWorker.register("/sw.js");

        setPermission(Notification.permission);
      } catch (_error) {
        setPermission("unsupported");
      }
    };

    registerServiceWorker();

    const handlePermissionChange = () => {
      if ("Notification" in window) {
        setPermission(Notification.permission);
      }
    };

    if (navigator.permissions) {
      navigator.permissions.query({ name: "notifications" }).then((status) => {
        setPermission(status.state as PermissionState);
        status.onchange = handlePermissionChange;
      });
      return;
    } else {
      const intervalId = setInterval(() => {
        if ("Notification" in window) {
          const currentPermission = Notification.permission;
          setPermission((prev) => (prev !== currentPermission ? currentPermission : prev));
        }
      }, 10000);
      return () => clearInterval(intervalId);
    }
  }, []);

  // 通知許可を要求する関数
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      toast.error(TOAST_MESSAGES.PUSH_NOTIFICATION.BROWSER_NOT_SUPPORTED);
      setPermission("unsupported");
      return false;
    }

    if (permission === "granted" || permission === "denied") {
      return permission === "granted";
    }

    try {
      const currentPermission = await Notification.requestPermission();
      setPermission(currentPermission);
      return currentPermission === "granted";
    } catch (_error) {
      setPermission("denied");
      return false;
    }
  }, [permission]);

  // プッシュ通知を送信する関数
  const sendNotification = useCallback(
    (
      message: string,
      title: string = PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.SUCCESS_TITLE,
    ) => {
      void postMessageToServiceWorker({
        title: title,
        message: message,
        icon: "/reqxignlogo.ico",
      });
    },
    [postMessageToServiceWorker],
  );

  // 拡張されたプッシュ通知を送信する関数
  const sendNotificationWithNavigation = useCallback(
    (data: NotificationData) => {
      void postMessageToServiceWorker({
        title: data.title || PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.SUCCESS_TITLE,
        message: data.message,
        icon: data.icon || "/reqxignlogo.ico",
        url: data.url,
        organization_id: data.organization_id,
        project_id: data.project_id,
        document_type: data.document_type,
        document_id: data.document_id,
      });
    },
    [postMessageToServiceWorker],
  );

  return {
    permission,
    requestPermission,
    sendNotification,
    sendNotificationWithNavigation,
  };
};
